import Cropper from 'cropperjs';
import { useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';

import { getUploadedImageContent, uploadImage } from '../../../../../attachments/attachmentActions';
import { updateElement } from '../../../../../actions/elementActions';
import { IMAGE_TYPES } from '../../../../../../../common/media/mediaConstants';
import { getCroppedImageBlobFromUrl } from '../../../content/utils/imageModalImageBlobUtils';
import type { GenericReduxDispatch } from '../../../../../../../common/actions/actionTypes';
import {
    buildBackgroundRemovalRenderedChanges,
    buildBackgroundRemovalCropChanges,
    buildBackgroundRemovalRevertChanges,
    getActiveBackgroundRemovalVariant,
    getRevertedImage,
    hasBackgroundRemovalCache,
    BackgroundRemovalVariantOptions,
} from '../../toolbar/imageModalBackgroundRemovalUtils';
import {
    dispatchBackgroundRemovalImageUpload,
    type BackgroundRemovalUploadResult,
} from '../../toolbar/imageModalBackgroundRemovalService';
import { getBackgroundRemovalCropImageData } from './backgroundRemovalCropUtils';
import type {
    ImMNElement,
    MNImageContent,
    MNImageEditorDataContent,
} from '../../../../../../../common/elements/elementModelTypes';

type UseBackgroundRemovalCropOptions = {
    element: ImMNElement;
    elementId: string;
    useSecureMediaUrl: boolean;
    renderSecureImagesThroughCF: boolean;
};

/**
 * Encapsulates background-removal state management during crop/rotate operations.
 */
export const useBackgroundRemovalCrop = ({
    element,
    elementId,
    useSecureMediaUrl,
    renderSecureImagesThroughCF,
}: UseBackgroundRemovalCropOptions) => {
    const dispatch = useDispatch<GenericReduxDispatch>();

    const hasBgRemovalCache = hasBackgroundRemovalCache(element);
    const activeVariant = getActiveBackgroundRemovalVariant(element);
    const inactiveVariant =
        activeVariant === BackgroundRemovalVariantOptions.withoutBackground
            ? BackgroundRemovalVariantOptions.withBackground
            : BackgroundRemovalVariantOptions.withoutBackground;
    const activeVariantIsTransparent = activeVariant === BackgroundRemovalVariantOptions.withoutBackground;
    const inactiveVariantIsTransparent = inactiveVariant === BackgroundRemovalVariantOptions.withoutBackground;

    const { backgroundRemovalEditSource, inactiveVariantImageUrl, inactiveVariantImageType, imageType } = useMemo(
        () =>
            getBackgroundRemovalCropImageData({
                element,
                elementId,
                useSecureMediaUrl,
                renderSecureImagesThroughCF,
            }),
        [element, elementId, useSecureMediaUrl, renderSecureImagesThroughCF],
    );

    const shouldIgnoreEditedImageFileSizeLimits = !!backgroundRemovalEditSource;

    const uploadCroppedBackgroundRemovalImage = useCallback(
        async (id: string, file: Blob) => {
            const result = await dispatchBackgroundRemovalImageUpload(dispatch, {
                elementId: id,
                file,
                ignoreFileSizeLimits: true,
            });

            if (!result) return result;
            const uploadedImage = getUploadedImageContent({
                ...result,
                transparent: activeVariantIsTransparent,
            }) as MNImageContent;

            dispatch(
                updateElement({
                    id,
                    changes: {
                        image: uploadedImage,
                        backgroundRemoval: buildBackgroundRemovalRenderedChanges({
                            element,
                            uploadedImage,
                            variant: activeVariant,
                        }),
                    },
                }),
            );

            return uploadedImage;
        },
        [activeVariant, activeVariantIsTransparent, dispatch, element],
    );

    const uploadEditedImage = useCallback(
        (id: string, file: Blob) => {
            if (hasBgRemovalCache) return uploadCroppedBackgroundRemovalImage(id, file);

            return dispatch(
                uploadImage({
                    id,
                    file,
                    attachmentData: undefined,
                    imageType: IMAGE_TYPES.ELEMENT,
                    options: {
                        ignoreFileSizeLimits: shouldIgnoreEditedImageFileSizeLimits,
                    },
                }),
            );
        },
        [dispatch, hasBgRemovalCache, shouldIgnoreEditedImageFileSizeLimits, uploadCroppedBackgroundRemovalImage],
    );

    /**
     * Mirror the active crop onto the hidden variant so toggling remains aligned after save.
     */
    const createAdditionalCroppedImageBlob = useCallback(
        (imageEditorData: Cropper.Data): Promise<Blob | null> => {
            if (!inactiveVariantImageUrl) return Promise.resolve(null);

            return getCroppedImageBlobFromUrl({
                imageUrl: inactiveVariantImageUrl,
                inputImageType: inactiveVariantImageType,
                outputImageType: inactiveVariantImageType,
                imageEditorData,
            });
        },
        [inactiveVariantImageUrl, inactiveVariantImageType],
    );

    /**
     * Store the mirrored hidden-side crop without changing which variant is currently active.
     */
    const uploadAdditionalCroppedImage = useCallback(
        async (id: string, file: Blob, activeRenderedImage?: MNImageContent) => {
            const result: BackgroundRemovalUploadResult | undefined = await dispatchBackgroundRemovalImageUpload(
                dispatch,
                {
                    elementId: id,
                    file,
                    ignoreFileSizeLimits: true,
                },
            );

            if (!result) return result;
            const uploadedImage = getUploadedImageContent({
                ...result,
                transparent: inactiveVariantIsTransparent,
            }) as MNImageContent;

            dispatch(
                updateElement({
                    id,
                    changes: {
                        backgroundRemoval: buildBackgroundRemovalRenderedChanges({
                            element,
                            uploadedImage,
                            variant: inactiveVariant,
                            additionalRenderedImage: activeRenderedImage,
                            additionalRenderedVariant: activeVariant,
                        }),
                    },
                }),
            );

            return uploadedImage;
        },
        [activeVariant, dispatch, element, inactiveVariant, inactiveVariantIsTransparent],
    );

    /**
     * Persist crop metadata while preserving the cached background-removal sources.
     */
    const buildCropSaveChanges = useCallback(
        (imageOriginal: MNImageContent, imageEditorData: MNImageEditorDataContent, clearDrawing: boolean) =>
            buildBackgroundRemovalCropChanges({
                element,
                imageOriginal,
                imageEditorData,
                clearDrawing,
                preserveBackgroundRemoval: hasBgRemovalCache,
            }),
        [hasBgRemovalCache, element],
    );

    /**
     * Revert crop metadata back to the full source image for the currently active variant.
     */
    const buildRevertChanges = useCallback(
        (imageOriginal: MNImageContent, clearDrawing: boolean) =>
            buildBackgroundRemovalRevertChanges({
                element,
                imageOriginal,
                clearDrawing,
                preserveBackgroundRemoval: hasBgRemovalCache,
            }),
        [hasBgRemovalCache, element],
    );

    const updateElementImageData = useCallback(
        (id: string, imageOriginal: MNImageContent, imageEditorData: MNImageEditorDataContent, clearDrawing: boolean) =>
            dispatch(
                updateElement({
                    id,
                    changes: buildCropSaveChanges(imageOriginal, imageEditorData, clearDrawing),
                    silent: true,
                    sync: true,
                }),
            ),
        [dispatch, buildCropSaveChanges],
    );

    const revertToOriginalImageData = useCallback(
        (id: string, imageOriginal: MNImageContent, clearDrawing: boolean) =>
            dispatch(
                updateElement({
                    id,
                    changes: buildRevertChanges(imageOriginal, clearDrawing),
                    silent: true,
                    sync: true,
                }),
            ),
        [dispatch, buildRevertChanges],
    );

    const getRevertImage = useCallback(() => getRevertedImage(element), [element]);

    return {
        backgroundRemovalEditSource,
        imageType,
        uploadEditedImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
        updateElementImageData,
        revertToOriginalImageData,
        getRevertImage,
    };
};
